// history removed in favor of native navigation
import { message } from 'antd';
import { refreshGoogleToken } from 'api-calls/refresh';
import { deleteGoogleToken, deleteToken, hasStaffSession } from 'utils/auth';

interface ApiOptions {
  endpoint: string;
  method: string;
  body?: any;
  streaming?: boolean;
  headers?: Record<string, string>;
  skipDefaultErrorHandling?: boolean;
  skipDefaultErrorMessage?: boolean;
}

// The error envelope ({success, error, error_code}) of a failed response, or
// undefined when the body isn't JSON (e.g. a proxy's 429 page).
const readErrorBody = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
};

// A 401 the client can recover from by refreshing the Google ID token: not
// one about the staff session itself.
const canRetryAfterRefresh = async (response: Response) => {
  if (!hasStaffSession()) return false;
  const body = await readErrorBody(response.clone());
  return body?.error_code !== 'staff_session_required';
};

export const apiCall = (apiName: string, fn: Function) => {
  const call = async (params: any, onStreaming?: (chunk: any) => void) => {
    // Built per attempt: headers carry the current Google ID token.
    const build = () => {
      const options: ApiOptions = fn(params);
      const fetchOptions: RequestInit = {
        method: options.method,
        headers: {
          ...options.headers,
        },
      };
      if (options.body) {
        fetchOptions.body = options.body;
      }
      return { options, fetchOptions };
    };
    let { options, fetchOptions } = build();

    if (options.streaming) {
      const response = await fetch(options.endpoint, fetchOptions);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (response.body && onStreaming) {
        const reader = response.body.getReader();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          onStreaming(value);
        }
        return;
      }
    }

    let response = await fetch(options.endpoint, fetchOptions);
    // The Google ID token expired (e.g. the laptop slept through the
    // refresh): refresh it once and retry.
    if (
      response.status === 401 &&
      !options.skipDefaultErrorHandling &&
      (await canRetryAfterRefresh(response)) &&
      (await refreshGoogleToken())
    ) {
      ({ options, fetchOptions } = build());
      response = await fetch(options.endpoint, fetchOptions);
    }
    if (response.ok) {
      if (response.status === 200 || response.status === 201) {
        const result = await response.json();
        return result;
      }

      return;
    }

    if (options.skipDefaultErrorHandling) {
      // The caller handles every error itself (including 401s), so hand it
      // the status and the response body (error, error_code) to decide with.
      const error: any = new Error(`HTTP error!`);
      error.status = response.status;
      error.response = await readErrorBody(response);
      throw error;
    }

    const { status } = response;

    if (status === 403) {
      const currentLocation = window.location.pathname;
      // Redirect to permission page with the current URL as a query parameter
      window.location.assign(
        `/permission?url=${encodeURIComponent(currentLocation)}`
      );
      return;
    }

    if (status === 401) {
      const currentLocation = window.location.pathname;
      const body = await readErrorBody(response);

      // Keep a live staff session: signing in with Google again then skips
      // the second factor.
      if (body?.error_code !== 'staff_session_required' && hasStaffSession()) {
        deleteGoogleToken();
      } else {
        deleteToken();
      }
      window.location.assign(
        `/login?url=${encodeURIComponent(currentLocation)}`
      );
      return;
    }

    if (!options.skipDefaultErrorMessage) {
      const result = await response.json();
      const errorMessage =
        typeof result === 'string'
          ? result
          : result?.message || result?.error || 'Request failed';

      message.error(errorMessage);

      // Throw error so it's caught by onError in mutations
      const error: any = new Error(errorMessage);
      error.status = status;
      error.response = result;
      throw error;
    }

    const error: any = new Error('Request failed');
    error.status = status;
    error.response = await readErrorBody(response);
    throw error;
  };

  return {
    queryKey: apiName,
    queryFn: call,
  };
};
